import React, { ReactNode } from 'react'
import { motion, Variants } from 'framer-motion'

export type SlideInViewDirection = 'up' | 'down' | 'left' | 'right'

export type SlideInViewProps = {
  children: ReactNode
  /** Direction from which the content slides in. Default: `'up'` */
  direction?: SlideInViewDirection
  /** How much of the element must be in view to trigger (0–1). Default: `0.15` */
  amount?: number
  /** Animate only the first time in view. Default: `true` */
  once?: boolean
  /** Animation duration in seconds. Default: `0.5` */
  duration?: number
  /** Delay before animation starts (seconds). Default: `0` */
  delay?: number
  /** Easing. Default: `'easeOut'` */
  ease?: 'easeOut' | 'easeIn' | 'easeInOut' | 'linear'
  /** Extra class for the wrapper (e.g. for layout). The wrapper is a block-level div. */
  className?: string
}

const directionOffset = 24

function getVariants(
  direction: SlideInViewDirection,
  duration: number,
  delay: number,
  ease: string
): Variants {
  const transition = { duration, delay, ease }
  const sign = direction === 'up' || direction === 'left' ? -1 : 1
  const x = direction === 'left' || direction === 'right' ? sign * directionOffset : 0
  const y = direction === 'up' || direction === 'down' ? sign * directionOffset : 0

  return {
    hidden: { opacity: 0, x, y },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition,
    },
  }
}

/**
 * Wraps content and animates it (slide + fade in) when it enters the viewport.
 * Uses framer-motion's Intersection Observer–based `whileInView`.
 */
function SlideInView({
  children,
  direction = 'down',
  amount = 0.15,
  once = true,
  duration = 0.5,
  delay = 0,
  ease = 'easeOut',
  className,
}: SlideInViewProps) {
  const variants = getVariants(direction, duration, delay, ease)

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount }}
      variants={variants}
    >
      {children}
    </motion.div>
  )
}

export default SlideInView
